import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";

import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";

import { TSidebarItem } from "@/constants/routes_sidebar/SidebarLinks";
import { cn, isCustomerPortal } from "@/lib/utils";
import { toggleMobileExpanded } from "@/redux/slices/configSlice";
import {
  hasCustomerReadPermission,
  hasReadPermission,
} from "@/redux/slices/permissionSlice";
import { RootState } from "@/redux/store";
import Icons from "@/utils/Icons";

export const NavLinkItem = ({
  sideBarData,
  handleCloseSheet,
  child,
  expanded,
  isHover,
  baseClass,
}: {
  sideBarData: TSidebarItem;
  handleCloseSheet: () => void;
  child: TSidebarItem;
  expanded: boolean;
  isHover: boolean;
  baseClass: string;
}): React.JSX.Element | null => {
  const hasPermission = useSelector(hasReadPermission(...child?.permission));

  if (!hasPermission) {
    return null;
  }

  return (
    <NavLink
      to={`${sideBarData?.value}${child?.value}`}
      onClick={handleCloseSheet}
      className={({ isActive }) => {
        return cn(
          baseClass,
          isActive && `bg-primaryLight text-primary `,
          "flex items-center justify-center p-0",
        );
      }}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              className={cn(
                "w-1 transition-all ease-in-out duration-300 h-1/2 bg-primary top-2.5 absolute left-0 rounded-e-full ",
              )}
            />
          )}

          <div
            className={cn(
              expanded || isHover ? "w-52 ml-3" : "hidden",
              sideBarData?.hasChild
                ? "flex w-full justify-between items-center"
                : "overflow-hidden",
            )}
          >
            <div className='flex items-center gap-x-2 my-1.5'>
              <Icons name='lollipop' color={isActive ? "#2744BD" : "#6B7280"} />
              <p
                className={cn(
                  "font-normal text-sm whitespace-nowrap",
                  !(expanded || isHover) && "hidden",
                )}
              >
                {child?.text}
              </p>
            </div>
          </div>
        </>
      )}
    </NavLink>
  );
};

export const SidebarItem = ({
  sideBarData,
}: {
  sideBarData: TSidebarItem;
}): React.JSX.Element | null => {
  const expanded = useSelector((state: RootState) => state.config.expanded);
  const mobileExpand = useSelector(
    (state: RootState) => state.config.mobileExpand,
  );
  const dispatch = useDispatch();
  const isHover = useSelector((state: RootState) => state.config.hover);
  const location = useLocation();

  const isActive =
    sideBarData.value === "/"
      ? location.pathname === "/"
      : location.pathname.includes(sideBarData.value as string);

  const baseClass =
    "relative flex hover:ml-2 transition-all duration-200 items-center p-2 select-none font-medium rounded-md cursor-pointer ease-in-out text-gray-600 hover:bg-primaryLight hover:text-primary ";
  const handleCloseSheet = (): void => {
    if (mobileExpand) {
      dispatch(toggleMobileExpanded());
    }
  };

  const hasPermission = useSelector(
    hasReadPermission(...sideBarData?.permission),
  );

  const hasCustomerPermission = useSelector(
    hasCustomerReadPermission(...sideBarData?.permission),
  );

  if (isCustomerPortal) {
    if (!hasCustomerPermission) {
      return null;
    }
  } else {
    if (!hasPermission && !hasCustomerPermission) {
      return null;
    }
  }

  if (!sideBarData?.hasChild || (!expanded && !isHover)) {
    const alterCondition =
      sideBarData?.alterValue &&
      location.pathname.includes(sideBarData?.alterValue);

    return (
      <NavLink
        to={sideBarData?.value as string}
        className={({ isActive }) => {
          return cn(
            "dark:text-gray-500 ",
            baseClass,
            (isActive || alterCondition) &&
              "bg-primaryLight hover:bg-primaryLight text-primary ",
            "flex items-center lg:justify-center",
          );
        }}
      >
        {(isActive || alterCondition) && (
          <span
            id='vertical-bar'
            className='w-1 transition-all ease-in-out duration-300 h-1/2 bg-primary top-2.5 absolute left-0 rounded-e-full'
          />
        )}
        <p>{sideBarData?.icon}</p>
        <div
          className={cn(
            "overflow-hidden",
            sideBarData?.hasChild && "flex w-full justify-between items-center",
            expanded || isHover ? "w-52 ml-3" : "hidden",
          )}
        >
          <span
            className={cn(
              "font-normal text-sm 3xl:text-base whitespace-nowrap",
              !(expanded || isHover) && "hidden",
            )}
          >
            {sideBarData?.text}
          </span>
        </div>
        {sideBarData?.alert && sideBarData.alert}
      </NavLink>
    );
  }

  return (
    <>
      <AccordionItem
        value={sideBarData?.value as string}
        className='border-b-0'
      >
        <AccordionTrigger
          className={cn(
            baseClass,
            `dark:text-gray-500 hover:no-underline  mb-0 hover:bg-primaryLight`,
            // isActive && "bg-primaryLight text-primary hover:bg-primaryLight ",
          )}
        >
          {/* {isActive && (
            <span
              id='vertical-bar'
              className='w-1 transition-all ease-in-out duration-300 h-1/2 bg-primary top-2.5 absolute left-0 rounded-e-full'
            />
          )} */}
          <div className='flex items-center gap-x-4'>
            <p className=''>{sideBarData?.icon}</p>
            <span
              className={cn(
                "font-normal text-sm whitespace-nowrap",
                !(expanded || isHover) && "hidden",
              )}
            >
              {sideBarData?.text}
            </span>
          </div>
        </AccordionTrigger>
        <AccordionContent className='py-0 mt-2'>
          {sideBarData?.children?.map((child, index) => {
            return (
              <NavLinkItem
                baseClass={baseClass}
                sideBarData={sideBarData}
                handleCloseSheet={handleCloseSheet}
                child={child}
                expanded={expanded}
                isHover={isHover}
                key={index}
              />
            );
          })}
        </AccordionContent>
      </AccordionItem>
    </>
  );
};
